import Logger from 'js-logger';
import moment from 'moment-timezone';

import TimeInterval from '@/models/interfaces/TimeInterval';

import { TableStates } from '../constants/TableConstants';
import { ReservationEvent, SeatedReservation } from '../models/interfaces/reservation/Reservation';

function earlierOrNotNullEvent(event1?: moment.Moment | null, event2?: moment.Moment | null) {
  if (!event1) {
    return event2;
  }

  if (!event2) {
    return event1;
  }

  return event1.isBefore(event2) ? event1 : event2;
}

export function createGetNotificationDate(reservationSetting: { notification: number }) {
  return function getNotificationDate(reservationDate: moment.Moment): moment.Moment {
    return reservationDate.clone().subtract(reservationSetting.notification, 'seconds');
  };
}

export function createGetReservationEndDate(reservationSetting: { duration: number[] }) {
  return function getReservationEndDate(reservationDate: moment.Moment, numberOfGuests: number) {
    let reservationDuration = 0;
    const { duration } = reservationSetting;
    if (numberOfGuests <= 2) {
      [reservationDuration] = duration;
    } else if (numberOfGuests < 7) {
      [, reservationDuration] = duration;
    } else {
      [, , reservationDuration] = duration;
    }

    return reservationDate.clone().add(reservationDuration, 'seconds');
  };
}

export function getNotificationEvent(reservation: SeatedReservation) {
  if (!reservation.reservationEvent.preReserve) {
    Logger.error(`Notification event must be present: ${reservation}!`);
    throw new Error('Notification event must be present!');
  }
  return reservation.reservationEvent.preReserve;
}

export function getReadyEvent(reservation: SeatedReservation) {
  return reservation.reservationEvent.ready;
}

export function getGuestWillArriveEvent(reservation: SeatedReservation) {
  if (!reservation.reservationEvent.guestWillArrive) {
    Logger.error(`GuestWillArrive event must be present: ${reservation}!`);
    throw new Error('GuestWillArrive event must be present!');
  }
  return reservation.reservationEvent.guestWillArrive;
}

export function getGuestArrivedEvent(reservation: SeatedReservation) {
  return reservation.reservationEvent.guestArrived;
}

export function getGuestWillLeaveEvent(reservation: SeatedReservation) {
  if (!reservation.reservationEvent.guestWillLeave) {
    Logger.error(`GuestWillLeave event must be present: ${reservation}!`);
    throw new Error('GuestWillLeave event must be present!');
  }
  return reservation.reservationEvent.guestWillLeave;
}

export function getGuestLeftEvent(reservation: SeatedReservation) {
  return reservation.reservationEvent.guestLeft;
}

export function hadGuestLeft(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  const leftEvent = getGuestLeftEvent(reservation);
  if (leftEvent && leftEvent.isSameOrBefore(currentDatetime)) {
    return true;
  }
  return false;
}

export function shouldGuestHadLeft(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  const willLeaveEvent = getGuestWillLeaveEvent(reservation);
  if (willLeaveEvent.isSameOrBefore(currentDatetime)) {
    return true;
  }
  return false;
}

export function isGuestAtTheTable(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  const arrivedEvent = getGuestArrivedEvent(reservation);
  if (!arrivedEvent) {
    return false;
  }

  const leftEvent = getGuestLeftEvent(reservation);
  const willLeaveEvent = getGuestWillLeaveEvent(reservation);
  const leaveEvent = earlierOrNotNullEvent(leftEvent, willLeaveEvent);
  if (arrivedEvent.isSameOrBefore(currentDatetime) && (!leaveEvent || leaveEvent.isAfter(currentDatetime))) {
    return true;
  }
  return false;
}

export function shouldGuestBeAtTheTable(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  const willArriveEvent = getGuestWillArriveEvent(reservation);
  const willLeaveEvent = getGuestWillLeaveEvent(reservation);
  if (willArriveEvent.isSameOrBefore(currentDatetime) && willLeaveEvent.isAfter(currentDatetime)) {
    return true;
  }
  return false;
}

export function isTableReady(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  const readyEvent = getReadyEvent(reservation);

  if (!readyEvent) {
    return false;
  }

  const willArriveEvent = getGuestWillArriveEvent(reservation);
  const arrivedEvent = getGuestArrivedEvent(reservation);
  const arriveEvent = earlierOrNotNullEvent(willArriveEvent, arrivedEvent);
  if (readyEvent.isSameOrBefore(currentDatetime) && !!arriveEvent && arriveEvent.isAfter(currentDatetime)) {
    return true;
  }

  return false;
}

export function hasReservationStarted(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  const notificationEvent = getNotificationEvent(reservation);

  if (!notificationEvent) {
    return false;
  }

  if (notificationEvent.isSameOrBefore(currentDatetime)) {
    return true;
  }

  return false;
}

export function isReservationActive(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  if (hadGuestLeft(reservation, currentDatetime)) {
    return false;
  }
  if (shouldGuestHadLeft(reservation, currentDatetime)) {
    return false;
  }

  if (isGuestAtTheTable(reservation, currentDatetime)) {
    return true;
  }

  if (shouldGuestBeAtTheTable(reservation, currentDatetime)) {
    return true;
  }
  if (isTableReady(reservation, currentDatetime)) {
    return true;
  }

  return false;
}

export function isReservationInNotificationStage(reservation: SeatedReservation, currentDatetime: moment.Moment): boolean {
  if (isReservationActive(reservation, currentDatetime)) {
    return false;
  }

  const notificationEvent = getNotificationEvent(reservation);
  const guestWillArriveEvent = getGuestWillArriveEvent(reservation);
  if (notificationEvent.isSameOrBefore(currentDatetime) && guestWillArriveEvent.isAfter(currentDatetime)) {
    return true;
  }

  return false;
}

export function getTableState(reservation: SeatedReservation | null, currentDatetime: moment.Moment): string {
  if (!reservation) {
    return TableStates.FREE;
  }

  if (hadGuestLeft(reservation, currentDatetime)) {
    return TableStates.FREE;
  }

  if (shouldGuestHadLeft(reservation, currentDatetime)) {
    return TableStates.FREE;
  }

  if (isGuestAtTheTable(reservation, currentDatetime)) {
    return TableStates.SEATED;
  }

  if (shouldGuestBeAtTheTable(reservation, currentDatetime)) {
    return TableStates.RESERVED;
  }

  if (isTableReady(reservation, currentDatetime)) {
    return TableStates.READY;
  }

  if (hasReservationStarted(reservation, currentDatetime)) {
    return TableStates.PREPARE;
  }

  return TableStates.FREE;
}

export function getNextReservation(reservations: SeatedReservation[], currentDatetime: moment.Moment): SeatedReservation | null {
  const filter = (reservation: SeatedReservation) => reservation.date.isAfter(currentDatetime) && !hadGuestLeft(reservation, currentDatetime);
  const futureReservations = reservations.filter(filter);

  if (futureReservations.length === 0) {
    return null;
  }

  if (futureReservations.length === 1) {
    return reservations[0];
  }

  let nextReservation = futureReservations[0];
  let timeUntilNextReservation = moment.duration(nextReservation.date.diff(currentDatetime));
  for (let i = 1; i < reservations.length; i += 1) {
    const timeUntilReservation = moment.duration(reservations[i].date.diff(currentDatetime));
    if (timeUntilReservation.asSeconds() < timeUntilNextReservation.asSeconds()) {
      nextReservation = reservations[i];
      timeUntilNextReservation = timeUntilReservation;
    }
  }

  return nextReservation;
}

export function canModifyState(reservation: SeatedReservation, newState: string, currentDatetime): boolean {
  const currentState = getTableState(reservation, currentDatetime);
  if (reservation && currentState === TableStates.FREE && newState === TableStates.READY) {
    return true;
  }
  if (reservation && currentState === TableStates.FREE && newState === TableStates.SEATED) {
    return true;
  }
  if (currentState === TableStates.RESERVED && newState === TableStates.SEATED) {
    return true;
  }
  if (currentState === TableStates.READY && newState === TableStates.SEATED) {
    return true;
  }
  if (currentState === TableStates.SEATED && newState === TableStates.FREE) {
    return true;
  }
  if (currentState === TableStates.FREE && newState === TableStates.RESERVED) {
    return true;
  }

  return false;
}

export function modifyState(reservation, fromState: string, newState: string, currentDatetime: moment.Moment): SeatedReservation {
  const event: ReservationEvent = reservation.reservationEvent;

  if (fromState === TableStates.FREE && newState === TableStates.READY) {
    event.ready = currentDatetime;
  }

  if (fromState === TableStates.FREE && newState === TableStates.SEATED) {
    event.guestArrived = currentDatetime;
  }

  if (fromState === TableStates.RESERVED && newState === TableStates.SEATED) {
    event.guestArrived = currentDatetime;
  }

  if (fromState === TableStates.READY && newState === TableStates.SEATED) {
    event.guestArrived = currentDatetime;
  }

  if (fromState === TableStates.SEATED && newState === TableStates.FREE) {
    event.guestLeft = currentDatetime;
  }

  return { ...reservation, reservationEvent: event };
}

export function getOptionalReservationTimeInterval(reservation: SeatedReservation): TimeInterval | undefined {
  return reservation.seating ?? reservation.timeInterval ?? undefined;
}

export function getReservationTimeInterval(reservation: SeatedReservation): TimeInterval {
  const interval = getOptionalReservationTimeInterval(reservation);
  if (interval !== undefined) return interval;
  throw new Error(`Reservation ${reservation.uuid} has no time interval`);
}
